"use client";

import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, type OfflineRapport } from "@/core/offline/db";

// ─── Textes pré-remplis (Tiptap JSON) ────────────────────────
// Renaud préfère modifier un texte existant plutôt que tout retaper.
// Ces templates correspondent au format standard de ses rapports.

function buildDefaultMissionText(siteName: string, siteAddress?: string | null) {
  const siteLabel = siteAddress ? `${siteName}, ${siteAddress}` : siteName;
  const dateFr = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [
          { type: "text", text: "La mission qui nous a été confiée, a été d'effectuer une série de mesures d'épaisseur par ultrasons, ainsi que des analyses d'eau physico-chimique avec recherche des bactéries de la bio-corrosion, pour le site " },
          { type: "text", marks: [{ type: "bold" }], text: siteLabel },
          { type: "text", text: "." },
        ],
      },
      {
        type: "paragraph",
        content: [
          { type: "text", text: "Ceci dans le but d'une recherche d'un diagnostic de l'état de corrosion de ces réseaux d'eaux." },
        ],
      },
      {
        type: "paragraph",
        content: [
          { type: "text", text: "Les mesures d'épaisseurs par ultrasons ont été effectuées sur site le " },
          { type: "text", marks: [{ type: "bold" }], text: dateFr },
          { type: "text", text: "." },
        ],
      },
    ],
  };
}

// Méthodologie type : fixée d'après le rapport "Immeuble Le Pyramide"
// finalisé par Renaud le 30/04/2026. Reprise à l'identique dans tous les
// nouveaux rapports. Structure Tiptap : titre H3 bleu/souligné, paragraphes
// justifiés, NB souligné, code couleur avec teintes bureautique
// (#00B050 vert, #F79646 orange, red, #C00000 rouge sombre).
const DEFAULT_METHODOLOGIE_TEXT = {
  type: "doc",
  content: [
    {
      type: "heading",
      attrs: { level: 3, textAlign: null },
      content: [
        {
          type: "text",
          text: "Mesure d'épaisseurs des canalisations par ultrasons",
          marks: [
            { type: "textStyle", attrs: { color: "#0000CC" } },
            { type: "underline" },
          ],
        },
        {
          type: "text",
          text: " :",
          marks: [{ type: "textStyle", attrs: { color: "#0000CC" } }],
        },
      ],
    },
    { type: "paragraph", attrs: { textAlign: null } },
    {
      type: "paragraph",
      attrs: { textAlign: null },
      content: [
        {
          type: "text",
          text: "Ce système est conçu pour être utilisé sur des pièces métalliques corrodées. Il permet de définir les épaisseurs des canalisations grâce aux mesures entre échos.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [
        {
          type: "text",
          text: "Sa précision est de 0,01 mm et s’affranchit des revêtements et couche de peinture.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [{ type: "text", text: " " }],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [
        {
          type: "text",
          text: "Ces analyses non destructives calcul l'épaisseur des parois des canalisations, sans mettre hors service les installations à vérifier, ni les vidanger.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [
        {
          type: "text",
          text: "L’aspect interne n’est pas visualisé par ce procédé et ne permet pas de définir un état de corrosion du réseau examiné.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [{ type: "text", text: " " }],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [
        {
          type: "text",
          text: "Le rapport qui suit vous est remis avec des résultats statistiques de l’épaisseur résiduelle de l’ensemble des canalisations.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [
        {
          type: "text",
          text: "Les mesures ont été effectuées par un mesureur d’épaisseurs par ultrasons à calibrage automatique géré par microprocesseur de type Sofranel EHC-09DL-CW couleur (effectué selon la norme ISO 16809).",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [{ type: "text", text: " " }],
    },
    {
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [
        { type: "text", text: "NB", marks: [{ type: "underline" }] },
        {
          type: "text",
          text: " : L’appareil de mesures ultrasons est étalonné, pendant chaque intervention, avec des cales de référence en acier doux non allié se rapportant au plus près de la nature des canalisations à mesurer.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: null },
      content: [
        {
          type: "text",
          text: "Plusieurs étalonnages ont été réalisés lors de l’intervention, afin d’éviter toute dérive de résultats.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: null },
      content: [
        {
          type: "text",
          text: "L’appareil est étalonné par un laboratoire de métrologie agrée.",
        },
      ],
    },
    {
      type: "paragraph",
      attrs: { textAlign: null },
      content: [{ type: "text", text: " " }],
    },
    {
      type: "paragraph",
      attrs: { textAlign: null },
      content: [
        {
          type: "text",
          text: "Code couleur employé pour interprétations des résultats",
          marks: [{ type: "underline" }],
        },
        { type: "text", text: " :" },
      ],
    },
    {
      // Les 4 lignes du code couleur en UN paragraphe (hardBreak = retour à la ligne sans espace)
      type: "paragraph",
      attrs: { textAlign: "justify" },
      content: [
        // Ligne Vert
        { type: "text", text: "En " },
        {
          type: "text",
          text: "Vert",
          marks: [
            { type: "textStyle", attrs: { color: "#00B050" } },
            { type: "bold" },
          ],
        },
        { type: "text", text: ", tuyauteries à conserver - corrosion de " },
        {
          type: "text",
          text: "0 à 19 %",
          marks: [
            { type: "textStyle", attrs: { color: "#00B050" } },
            { type: "bold" },
          ],
        },
        { type: "hardBreak" },
        // Ligne Orange
        { type: "text", text: "En " },
        {
          type: "text",
          text: "Orange",
          marks: [
            { type: "textStyle", attrs: { color: "#F79646" } },
            { type: "bold" },
          ],
        },
        {
          type: "text",
          text: ", tuyauteries à surveiller tous les 2/3 ans, avec possibilité de sauvegarde - corrosion de ",
        },
        {
          type: "text",
          text: "20 à 29 %",
          marks: [
            { type: "textStyle", attrs: { color: "#F79646" } },
            { type: "bold" },
          ],
        },
        { type: "hardBreak" },
        // Ligne Rouge
        { type: "text", text: "En " },
        {
          type: "text",
          text: "Rouge",
          marks: [
            { type: "textStyle", attrs: { color: "red" } },
            { type: "bold" },
          ],
        },
        {
          type: "text",
          text: ", tuyauteries à remplacer car risques de percements ponctuels existants - corrosion de ",
        },
        {
          type: "text",
          text: "30 à 39 %",
          marks: [
            { type: "textStyle", attrs: { color: "red" } },
            { type: "bold" },
          ],
        },
        { type: "hardBreak" },
        // Ligne Rouge sombre
        { type: "text", text: "En " },
        {
          type: "text",
          text: "Rouge sombre",
          marks: [
            { type: "textStyle", attrs: { color: "#C00000" } },
            { type: "bold" },
            { type: "underline" },
          ],
        },
        {
          type: "text",
          text: ", tuyauteries à remplacer impérativement et rapidement - corrosion ",
        },
        {
          type: "text",
          text: "au-dessus de 40 %",
          marks: [
            { type: "textStyle", attrs: { color: "#C00000" } },
            { type: "bold" },
            { type: "underline" },
          ],
        },
      ],
    },
  ],
};

function buildDefaultConclusionsText(siteName: string) {
  return {
    type: "doc",
    content: [
      {
        type: "heading",
        attrs: { level: 3 },
        content: [
          { type: "text", text: "Contrôle de mesures d'épaisseurs par ultrasons :" },
        ],
      },
      {
        type: "paragraph",
        content: [
          { type: "text", text: "Les mesures effectuées sur les réseaux du site " },
          { type: "text", marks: [{ type: "bold" }], text: siteName },
          { type: "text", text: " montrent des pertes d'épaisseurs " },
          { type: "text", marks: [{ type: "bold" }], text: "hétérogènes" },
          { type: "text", text: "." },
        ],
      },
      {
        type: "paragraph",
        content: [
          { type: "text", text: "Les réseaux devront être inspectés tous les 2/3 ans." },
        ],
      },
    ],
  };
}

/**
 * Hook CRUD pour les rapports. Un seul rapport par site.
 * Le rapport est créé automatiquement à la première visite de la page d'édition.
 */
export function useRapport(siteId: string) {
  // Réactif : se met à jour quand le rapport change dans Dexie
  const rapport = useLiveQuery(
    () => offlineDb.rapports.where("siteId").equals(siteId).first(),
    [siteId]
  );

  /** Crée le rapport pour ce site (s'il n'existe pas déjà) */
  async function createRapport(data?: {
    numero?: string;
    signataireNom?: string;
    signataireFonction?: string;
  }): Promise<string> {
    // Vérifier qu'il n'existe pas déjà
    const existing = await offlineDb.rapports
      .where("siteId")
      .equals(siteId)
      .first();
    if (existing) return existing.id;

    const id = crypto.randomUUID();
    const now = new Date();

    // Charger le site pour injecter nom + adresse dans les textes
    const site = await offlineDb.sites.get(siteId);
    const siteName = site?.name ?? "";
    const siteAddress = site?.address;

    await offlineDb.rapports.add({
      id,
      numero: data?.numero ?? "",
      missionText: buildDefaultMissionText(siteName, siteAddress),
      methodologieText: DEFAULT_METHODOLOGIE_TEXT,
      conclusionsText: buildDefaultConclusionsText(siteName),
      siteId,
      signataireNom: data?.signataireNom ?? "Renaud MERLE",
      signataireFonction: data?.signataireFonction ?? "Gérant",
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending",
    });

    return id;
  }

  /** Met à jour un ou plusieurs champs du rapport */
  async function updateRapport(
    data: Partial<
      Pick<
        OfflineRapport,
        | "numero"
        | "missionText"
        | "methodologieText"
        | "conclusionsText"
        | "destinataire"
        | "destinataireAdresse"
        | "signataireNom"
        | "signataireFonction"
        | "titreCouverture"
        | "sousTitreReseau"
        | "batiment"
        | "notaBene"
        | "customSections"
        | "hiddenSections"
      >
    >
  ) {
    if (!rapport) return;
    await offlineDb.rapports.update(rapport.id, {
      ...data,
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }

  return { rapport: rapport ?? null, createRapport, updateRapport };
}
